import React, { useCallback, useRef, useState } from 'react';
import { Input, Popover } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useConfigColumnSearch } from './columnSearchContext';

/** Fixed width for the search field in the popover (not tied to column width). */
const SEARCH_INPUT_WIDTH_PX = 200;

const stopPropagation = (e: React.SyntheticEvent) => {
  e.stopPropagation();
};

type Props = {
  column?: { title?: React.ReactNode; dataKey?: string };
};

function ColumnFilterHeader({ column }: Props) {
  const { columnSearchTexts, setColumnSearchText } = useConfigColumnSearch();
  const dataKey = column?.dataKey;
  const value = dataKey != null && dataKey !== '' ? columnSearchTexts[dataKey] ?? '' : '';
  const [open, setOpen] = useState(false);
  const inputRef = useRef<InputRef>(null);

  const handleOpenChange = useCallback((visible: boolean) => {
    setOpen(visible);
    if (visible) {
      setTimeout(() => {
        inputRef.current?.focus({ cursor: 'start' });
      });
    }
  }, []);

  const searchPanel = (
    <div
      style={{
        width: SEARCH_INPUT_WIDTH_PX,
        padding: 8,
        boxSizing: 'border-box',
      }}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      <Input
        ref={inputRef}
        size="small"
        placeholder="Search..."
        value={value}
        allowClear
        style={{ width: '100%' }}
        onChange={(e) =>
          dataKey != null && dataKey !== '' && setColumnSearchText(dataKey, e.target.value)
        }
      />
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 6,
        width: '100%',
        minWidth: 0,
        padding: '2px 0',
        boxSizing: 'border-box',
      }}
    >
      <span
        className="BaseTable__header-cell-text"
        style={{
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          flex: 1,
        }}
      >
        {column?.title}
      </span>
      {dataKey != null && dataKey !== '' ? (
        <span
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', padding: '0 2px' }}
        >
          <Popover
            content={searchPanel}
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
            arrow={false}
            overlayInnerStyle={{ padding: 0 }}
          >
            <SearchOutlined
              aria-label="Filter column"
              style={{
                fontSize: 12,
                color: value ? '#1890ff' : '#bfbfbf',
                cursor: 'pointer',
              }}
            />
          </Popover>
        </span>
      ) : null}
    </div>
  );
}

export default ColumnFilterHeader;
